import React from 'react';
import { IKnownVendor, IVendorSettings } from '../../models/Settings';
import { DefaultButton, MessageBar, MessageBarType, Panel, PrimaryButton, Stack, TextField } from '@fluentui/react';
import { usePutVendorSettingsMutation } from '../../store/Api';
import { useGetVendorMappableFoliosQuery } from '../../store/endpoints/RecordLinksEndpoints';
import { IsMutationSuccess } from '../../logic/MutationTypeChecker';
import { IActionResponse } from '../../dal/BaseDAL';
import ToggleField from "../Common/ToggleField";
import Setting, { DynamicSetting } from '../Common/Setting';
import FolioVendorPicker from "./FolioVendorPicker";
import VendorSelectionDropdown from "./VendorSelectionDropdown";

interface IVendorManagementEditPanelProps {
    vendor?: IVendorSettings;
    isOpen: boolean;
    onClose: () => void;
}

const VendorManagementEditPanel: React.FC<IVendorManagementEditPanelProps> = (props) => {
    const [vendor, setVendor] = React.useState<IVendorSettings>(props.vendor ?? {sourceVendor: '', enabled: true});
    const [saveVendor] = usePutVendorSettingsMutation();
    const [error, setError] = React.useState<string | undefined>();
    const [displayNamePrefilled, setDisplayNamePrefilled] = React.useState<boolean>(false);
    const mappableFolios = useGetVendorMappableFoliosQuery();

    // Prefill the display name from the selected known vendor, but never clobber hand-typed input: only fill
    // when the current display name is empty or was itself set by a previous prefill. Switching to a vendor
    // without a display name (or to Custom) clears a stale prefill so it can't be saved for the wrong vendor.
    // Reading vendor.vendorDisplayName from the closure is safe: the only same-batch update is sourceVendor.
    const onKnownVendorSelected = (known: IKnownVendor | undefined) => {
        const canPrefill = !vendor.vendorDisplayName || displayNamePrefilled;
        if (known?.vendorDisplayName && canPrefill) {
            setVendor(prev => ({...prev, vendorDisplayName: known.vendorDisplayName}));
            setDisplayNamePrefilled(true);
        } else if (!known?.vendorDisplayName && displayNamePrefilled) {
            setVendor(prev => ({...prev, vendorDisplayName: undefined}));
            setDisplayNamePrefilled(false);
        }
    };

    const setMapping = (folioId: string, externalId: string | undefined) => {
        setVendor((v) => {
            const crmVendorMappings = {...(v.crmVendorMappings ?? {})};
            if (externalId) crmVendorMappings[folioId] = externalId;
            else delete crmVendorMappings[folioId];
            return {...v, crmVendorMappings};
        });
    };

    React.useEffect(() => {
        setVendor(props.vendor ?? {sourceVendor: '', enabled: true});
        setDisplayNamePrefilled(false);
    }, [props.vendor]);

    const save = async () => {
        if (!vendor) return;
        if (!vendor.sourceVendor) {
            setError('Vendor Name is required');
            return;
        }
        const result = await saveVendor({vendor});
        if (IsMutationSuccess<IActionResponse>(result)) {
            if (result.data.success) {
                props.onClose();
            } else {
                setError(result.data.message ?? 'An error occurred');
            }
        } else {
            setError('An unknown error occurred');
        }
    }

    const renderFooter = () => (<Stack horizontal tokens={{childrenGap: '1em'}}>
        <PrimaryButton onClick={save}>Save</PrimaryButton>
        <DefaultButton onClick={props.onClose}>Cancel</DefaultButton>
        </Stack>);

    const renderHeader = () => (<Stack tokens={{childrenGap: '1em'}} grow style={{paddingLeft: '25px'}}>
        <h1>{props.vendor ? 'Edit Vendor' : 'Add Vendor'}</h1>
        {error && <MessageBar messageBarType={MessageBarType.error}>{error}</MessageBar>}
        </Stack>);

    return (
        <Panel
            isOpen={props.isOpen}
            onDismiss={props.onClose}
            closeButtonAriaLabel='Close'
            onRenderFooterContent={renderFooter}
            onRenderHeader={renderHeader}
            headerText={props.vendor ? 'Edit Vendor' : 'Add Vendor'}
            isFooterAtBottom={true}
        >
            <Stack tokens={{childrenGap: '1em'}}>
                {props.vendor
                    ? <Setting id='vendors.details.sourceVendor'><TextField label='Vendor Name' value={vendor.sourceVendor} onChange={(e, v) => setVendor({...vendor, sourceVendor: v ?? ''})} /></Setting>
                    : <VendorSelectionDropdown
                        label='Vendor Name'
                        settingId='vendors.details.sourceVendor'
                        knownVendorsOnly
                        allowCustom
                        selectedVendor={vendor.sourceVendor}
                        onChange={(v) => setVendor(prev => ({...prev, sourceVendor: v}))}
                        onKnownVendorSelected={onKnownVendorSelected}
                    />}
                <Setting id='vendors.details.vendorDisplayName'><TextField label='Display Name' value={vendor.vendorDisplayName} onChange={(e, v) => { setVendor({...vendor, vendorDisplayName: v}); setDisplayNamePrefilled(false); }} /></Setting>
                <ToggleField settingId='vendors.details.enabled' inline={true} label='Enabled' checked={vendor.enabled} onChange={(e, c) => setVendor({...vendor, enabled: c})} />
                <Setting id='vendors.details.punchoutVendorName'><TextField label='Punchout Name' value={vendor.punchoutVendorName} onChange={(e, v) => setVendor({...vendor, punchoutVendorName: v})} /></Setting>
                {(mappableFolios.data?.length ?? 0) > 0 && (
                    <Stack tokens={{childrenGap: '0.5em'}}>
                        <h3 style={{marginBottom: 0}}>CRM Vendor Mappings</h3>
                        <span>Map this vendor to its matching vendor in each connected CRM.</span>
                        {/* One row per connected CRM, so the id has to name the folio to be linkable
                            at all — a support link to "the ConnectWise mapping" can only be written
                            once the folio id is known. Same reasoning as the other dynamic ids. */}
                        {mappableFolios.data?.map((folio) => (
                            <DynamicSetting key={folio.folioId} id={`vendors.folioMapping.${folio.folioId}`}>
                            <FolioVendorPicker
                                folioId={folio.folioId}
                                folioName={folio.folioName}
                                value={vendor.crmVendorMappings?.[folio.folioId]}
                                onChange={(externalId) => setMapping(folio.folioId, externalId)}
                            />
                            </DynamicSetting>
                        ))}
                    </Stack>
                )}
            </Stack>
        </Panel>
    );
}

export default VendorManagementEditPanel;